import { RepeatIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, Editor, TLShapeUtilCanBindOpts } from "tldraw";

import {
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
  BlockBody,
} from "@/components/canvas-editor/components/Container";
import { CanvasInput } from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  Dimensions,
  EachProps,
  EachShape,
  pinnableShapes,
  metadataType,
  eachType,
} from "@/components/canvas-editor/shapes/shapes";
import { ToolStateNode } from "@/components/canvas-editor/utils";
import { cn } from "@/lib/utils";

export class EachTool extends ToolStateNode {
  static override id = eachType;

  static override uiToolDef = (editor: Editor) => ({
    id: eachType,
    icon: RepeatIcon,
    label: "Each",
    defaultColor: EachShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(eachType),
    docs: () => (
      <NodeDocs editor={editor} component={Each} util={EachShapeUtil} />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: eachType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class EachShapeUtil extends BindableUtil<EachShape> {
  static override migrations = createMigrations(eachType);
  static override type = eachType;

  static override docs = {
    defaultColor: "blue" as const,
    title: "Each",
    description: "Use this node to iterate over a list parameter.",
  };

  override getType() {
    return eachType;
  }

  static override props: RecordProps<EachShape> = {
    w: T.number,
    minW: T.number,
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    metadata: metadataType,
    var: T.string,
    padding: T.object({
      top: T.number,
      left: T.number,
      right: T.number,
      bottom: T.number,
      gap: T.number,
    }),
  };

  override getDefaultProps() {
    return {
      w: 320,
      minW: 320,
      h: 101,
      var: "",
      padding: {
        top: 98,
        bottom: 16,
        right: 16,
        left: 16,
        gap: 14,
      },
    };
  }

  override canBind({
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<EachShape>) {
    if (bindingType === "arrow") {
      return true;
    }

    return pinnableShapes.includes(toShapeType) && bindingType === "layout";
  }

  override component(shape: EachShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <Each
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function Each({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: EachShape;
  className?: string;
  updateShape: (shape: EachShape, props: Partial<EachProps>) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        isBound={isBound}
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        className={cn(className)}
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={shape.props.h}
        variant={shape.props.metadata?.color || "yellow"}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={{
            title: "Each",
            description:
              "This node can be used to iterate over a list parameter.",
            variant: "yellow",
          }}
        />
        <BlockBody className="p-4 flex gap-2">
          <CanvasInput
            aria-label="Custom Node Parameter"
            value={shape.props.var}
            placeholder="Parameter"
            className="flex-1"
            onValueChange={(v) => updateShape(shape, { var: v })}
            onInput={(e) => {
              if (!e.currentTarget.value.match(/^\$/)) {
                e.currentTarget.setCustomValidity(
                  "You must use a custom node parameter. Use $ to reference a one.",
                );
                e.currentTarget.reportValidity();
              } else {
                e.currentTarget.setCustomValidity("");
                e.currentTarget.reportValidity();
              }
            }}
          />
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
